/**
 * PREFACTOR-grammar — the adapter between the two `WidgetDefinition` grammars.
 *
 * ## The defect this exists to close
 *
 * This repo carries TWO mutually-invalid widget grammars, and until now nothing
 * translated between them:
 *
 * | | **domain / wire** (`UiWidgetSchema`) | **builder form** (`WidgetDefinition`) |
 * |---|---|---|
 * | data source | *(absent — derived from the members)* | `cube: string` **required** |
 * | dimensions | `string[]` | `{ field, label? }[]` |
 * | filters | `{ member, operator: 'equals'\|…, values[] }` | `{ field, operator: 'eq'\|…, value }` |
 * | time | `timeDimensions[{ dimension, granularity, dateRange }]` | `dateRange: { field, relativeRange, granularity }` |
 *
 * The domain grammar is enforced by the PV3 aggregate (`AddUiWidgetLayout` /
 * `ChangeUiWidgetLayout` hard-validate their body — `S.shape()` defaults to
 * `additionalProperties: false` and PV3's ajv has no `removeAdditional`) AND by
 * the LLM output validator. The builder grammar is enforced by
 * `parseWidgetDefinition` at render.
 *
 * Consequence, measured against the real schema with PV3's real ajv config
 * (`__tests__` in `src/tests/src/analytics/widgetGrammar.adapter.test.ts`):
 *
 *  - **Every** builder-form layout is REJECTED by the aggregate — not just ones
 *    with dimensions or filters. `cube` is unconditionally present and is
 *    unconditionally an additional property, so even a bare KPI fails. The manual
 *    report builder has therefore never been able to save a single widget.
 *  - A domain-form layout THROWS in `parseWidgetDefinition` (`cube is required`),
 *    and if it didn't, `widgetDefinitionToCubeQuery` would silently drop every
 *    dimension (`d.field` on a string is `undefined`), every filter and the whole
 *    date range — issuing a dimensionless, whole-history query and rendering a
 *    **wrong-but-plausible** chart.
 *
 * ## The contract
 *
 * **The domain/wire grammar is canonical.** It is what the LLM emits, what the
 * server validates and what Cube speaks. The builder form is a UI convenience,
 * and this module is the seam where one becomes the other:
 *
 *  - `toBuilderLayout` on the way IN (readmodel / LLM -> render + edit);
 *  - `toDomainLayout`  on the way OUT (edit -> `AddUiWidgetLayout`).
 *
 * `WidgetLayoutComponent` owns both call sites; nothing else should need this.
 *
 * ## The seam has TWO halves, and the envelope is the one that gets forgotten
 *
 * A layout widget is `{ x, y, width, height, tint, definition }`. Everything above
 * is about `definition`; the ENVELOPE around it is rebuilt separately, by
 * `pickWidgetEnvelope`, and it is a whitelist for the same reason
 * (`additionalProperties: false`). A key added to the envelope schema and not to
 * that whitelist is dropped on every save — silently, because the value is still
 * in memory for the rest of the session and only disappears on reload. That is
 * exactly what happened to `tint` between tickets 20 and 34: the domain schema was
 * right the whole time and the loss was entirely here. Each envelope key is
 * carried under a guard matched to ITS type — see `pickWidgetEnvelope`.
 *
 * ## What is dropped, and why it is safe
 *
 * | dropped on save | why it costs nothing |
 * |---|---|
 * | `cube` | derivable — it is the member prefix (`Sales.netRevenue` -> `Sales`); `widgetDefinitionToCubeQuery` never reads it |
 * | `metrics` | **provably dead**: the Metrics section writes `config.measures` (a `string[]`); nothing ever populates or reads `metrics` |
 * | `dimensions[].label` / `.having`, `filters[].label` | display-only; `Dimensions.tsx` renders `field`, never `label` |
 * | `granularity.value` | already dead on the query path — `builderDateRangeToTimeDimension` reads `granularity.unit` only |
 * | readmodel extras (`lastEvent`, `isActive`) | not in `UiWidgetsLayoutSchema`; sending them is itself a 400 (see `toDomainLayout`) |
 *
 * ## Round-trip guarantees (pinned by the oracle)
 *
 *  - `toDomainWidget(toBuilderWidget(d))` is **byte-identical** to `d` for every
 *    domain widget. This is the direction that matters: it is the LLM's output
 *    and the curated defaults, and it is what three stacked tickets depend on.
 *  - `toBuilderWidget(toDomainWidget(b))` is **query-identical** to `b`, not
 *    byte-identical: the display-only fields above are gone, and `in`/`nin`
 *    normalise to `eq`/`neq` (both already mean Cube `equals`/`notEquals`, so the
 *    emitted query is unchanged).
 *
 * ## ⚠️ The one thing this adapter deliberately does NOT do
 *
 * **It does not reinterpret date semantics.** A range expression
 * (`timeDimensions[].dateRange`) is carried VERBATIM in both directions — the
 * builder's token (`'1mo'`), a Cube phrase (`'last 30 days'`), or a concrete
 * `[ start, end ]`. `resolveDateRangeWindow` stays the single place an expression
 * becomes a window.
 *
 * Rejected: translating the builder's tokens into Cube phrases so the persisted
 * layout is Cube-native end to end. `resolveRelativeRange` produces a **rolling
 * window ending NOW, at full timestamp precision** (deliberately — `'15m'` and
 * `'1h'` are meaningless at day precision), whereas Cube's `last 1 month` snaps
 * to day boundaries. The translation would therefore silently change the numbers
 * on every builder-authored widget, which is precisely the class of failure this
 * prefactor exists to eliminate.
 *
 * **The cost, stated plainly:** a persisted layout may carry an FE-dialect token
 * that Cube itself cannot parse. The front end is safe (it always resolves via
 * `resolveDateRangeWindow`), and the LLM never emits tokens (its prompt teaches
 * Cube phrases). But any future SERVER-side consumer that hands
 * `timeDimensions[].dateRange` straight to Cube must resolve tokens first.
 */

import type { UiWidgetsLayoutType } from '@bett3r-dev/teselly-domain';
import type {
  DateRangeItem,
  DimensionItem,
  FilterItem,
  FilterOperator,
  OrderByItem,
  WidgetDefinition
} from './WidgetBuilder/types';
import { isDatalessWidgetType, isReadmodelWidgetType, type WidgetType } from './types';
// `tintTokens`, NOT `tints` — the latter imports `WidgetCard.module.css`, and the
// root jest runner that owns this module's ajv oracle
// (`src/tests/src/analytics/widgetGrammar.adapter.test.ts`) has no CSS
// `moduleNameMapper`, so importing it here takes the whole oracle out with a
// `SyntaxError` on the stylesheet. Same single definition either way — `tints.ts`
// re-exports these. See `tintTokens.ts`.
import { isTintValue } from '../WidgetCard/tintTokens';
import { FILTER_OPERATOR_MAP } from './widgetDefinition';

// ---------------------------------------------------------------------------
// The domain (wire) shape, structurally
// ---------------------------------------------------------------------------

/**
 * The domain widget, as a plain structural type.
 *
 * `UiWidgetDefinitionType` is a 10-branch `anyOf` union out of jsonschema-definer
 * and is not narrowable in any useful way here. The real gate is ajv against the
 * real schema, and that is exactly what the oracle asserts — this type only has
 * to be honest about the field names.
 */
export type DomainTimeDimension = {
  dimension: string;
  granularity?: string;
  dateRange?: string | string[];
  compareDateRange?: ( string | string[])[];
  compare?: boolean;
};

export type DomainFilter = {
  member: string;
  operator: string;
  values?: string[];
};

export type DomainWidgetDefinition = {
  id: string;
  name: string;
  title?: string;
  description?: string;
  type: string;
  measures?: string[];
  dimensions?: string[];
  timeDimensions?: DomainTimeDimension[];
  filters?: DomainFilter[];
  orderBy?: OrderByItem[];
  limit?: number;
  offset?: number;
  widgetComponentProps: Record<string, any>;

  /**
   * TV1-2291 (§6) — the READMODEL binding (`UiWidgetReadmodelBaseSchema`, ticket 23).
   * Present only on a `count` / `alert` / `list` / `progress` widget; those extend
   * `UiWidgetBaseSchema`, so they carry NONE of the Cube fields above.
   *
   * ⚠️ `filter` is the readmodel grammar (`{ field, op, value }`) and is a DIFFERENT
   * field from the Cube-grammar `filters`. Nothing translates between them.
   *
   * ⚠️ There is deliberately NO top-level `severity` / `href` here (t34). `alert`
   * declares both INSIDE `widgetComponentProps` — see `READMODEL_WIDGET_KEYS` for
   * why that placement is load-bearing and why declaring them here would be a
   * standing invitation to re-add them to that list.
   */
  collection?: string;
  filter?: unknown[];
};

export type DomainLayoutWidget = {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  definition: DomainWidgetDefinition;
};

export type DomainLayout = {
  id: string;
  parentId?: string | null;
  type: 'layout' | 'folder';
  color?: string;
  icon?: string;
  name: string;
  description?: string;
  widgets?: DomainLayoutWidget[];
  device?: 'web' | 'mobile';
  cols?: number;
  widthPx?: number;
};

// ---------------------------------------------------------------------------
// Operator mapping
// ---------------------------------------------------------------------------

/**
 * Cube operator -> builder operator.
 *
 * TOTAL over `CubeFilterSchema`'s eighteen operators, which is the point: an
 * LLM-generated or curated filter using `contains` / `inDateRange` previously had
 * nowhere to land, and the alternatives were to drop it (a chart computed over
 * unfiltered data — wrong, and plausible) or to fail the whole widget. The four
 * that have a builder short form get it, so the Filters section renders them
 * normally; the rest are carried verbatim and map to themselves on the way back
 * out (`FILTER_OPERATOR_MAP`).
 */
const DOMAIN_TO_BUILDER_OPERATOR: Record<string, FilterOperator> = {
  equals: 'eq',
  notEquals: 'neq',
  set: 'exists',
  notSet: 'notExists',
  lt: 'lt',
  gt: 'gt',
  lte: 'lte',
  gte: 'gte',
  contains: 'contains',
  notContains: 'notContains',
  startsWith: 'startsWith',
  endsWith: 'endsWith',
  inDateRange: 'inDateRange',
  notInDateRange: 'notInDateRange',
  beforeDate: 'beforeDate',
  beforeOrOnDate: 'beforeOrOnDate',
  afterDate: 'afterDate',
  afterOrOnDate: 'afterOrOnDate'
};

/** Cube operators that carry no values (`values` must be OMITTED, not `[]`). */
const DOMAIN_UNARY_OPERATORS: ReadonlySet<string> = new Set([ 'set', 'notSet' ]);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** `'Sales.netRevenue'` -> `'Sales'`. The builder's `cube` is nothing more than this. */
const cubeOfMember = ( member: string | undefined ): string =>
  typeof member === 'string' && member.includes( '.' ) ? member.split( '.' )[0] : '';

/**
 * The builder's `cube`, re-derived from the members a domain widget actually
 * carries. Measures first (every data widget has at least one — both
 * `parseWidgetDefinition` and the KPI validator insist), then dimensions, then
 * the time dimension. Empty string for a widget with no members at all, which
 * `parseWidgetDefinition` will then reject LOUDLY — the correct outcome, since a
 * data widget without a data source is not renderable in any grammar.
 */
const deriveCube = ( widget: DomainWidgetDefinition ): string =>
  cubeOfMember( widget.measures?.[0])
  || cubeOfMember( widget.dimensions?.[0])
  || cubeOfMember( widget.timeDimensions?.[0]?.dimension );

/** Copy `key` onto `target` only when `value` is meaningfully present. */
const setIfPresent = <T extends Record<string, any>>( target: T, key: string, value: unknown ): void => {
  if ( value === undefined || value === null || value === '' ) return;
  if ( Array.isArray( value ) && value.length === 0 ) return;
  ( target as Record<string, any> )[key] = value;
};

/**
 * TV1-2291 (§6) — the TOP-LEVEL keys a READMODEL widget carries beyond the shared
 * envelope. All three are `UiWidgetReadmodelBaseSchema`: `collection` / `filter` /
 * `limit`.
 *
 * They cross the seam together: this is the exact counterpart of the
 * `isDatalessWidgetType` early-returns below, and it is what makes the fetch branch
 * reachable at all. `toBuilderWidget` builds a FRESH object with a fixed key set, so
 * without this list the binding is silently dropped on the way IN — `getWidgetData`
 * would then see a `count` widget with no `collection` and every readmodel tile
 * would be an error tile.
 *
 * On the way OUT the same list is what keeps a round-tripped widget saveable:
 * `UiWidgetsLayoutSchema` is `additionalProperties: false` AND requires `collection`,
 * so a definition that lost it fails the pre-dispatch ajv check.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠️ `severity` AND `href` ARE NOT HERE, AND MUST NOT BE ADDED (t34, found by t25).
 *
 * They were listed here through ticket 24 and it was wrong. `UiWidgetAlertSchema`
 * (ticket 23) declares BOTH **inside `widgetComponentProps`**, not at the top level:
 *
 *     widgetComponentProps: S.shape({
 *       severity: S.string().enum( 'low', 'medium', 'high' ),
 *       href: S.string().optional()
 *     }).additionalProperties( true )
 *
 * So copying them top-level was a) dead — nothing writes them there — and b) a
 * loaded gun: a definition that DID carry a top-level `severity` would have it
 * faithfully re-emitted onto the wire, where `additionalProperties: false` rejects
 * it. The failure surfaces as an opaque ajv error inside `handleLayoutChange` with
 * nothing pointing back at this list.
 *
 * They need no entry at all: `widgetComponentProps` is copied WHOLESALE in both
 * directions (`widget.widgetComponentProps ?? {}`, set at construction before the
 * readmodel branch returns), so the props bag — including these two — round-trips
 * untouched. That is the fix, and it is why the list is now query-keys-only.
 *
 * ⚠️ The reverse "fix" — moving them top-level in the SCHEMA — is barred. Ticket 20
 * established that `widgetGrammar.modelVariant()` omits exactly `id` and
 * `widgetComponentProps`, so ANY other top-level definition field is inherited into
 * the structured-output schema handed to the LLM. A top-level `href` hands a
 * language model a raw navigation target — and `href` is the field TV1-1934 was a
 * stored XSS through. Inside the props bag is where ticket 23 deliberately put them.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * ⚠️ Add a key here ONLY when a readmodel widget schema gains a TOP-LEVEL one —
 * i.e. a QUERY key on `UiWidgetReadmodelBaseSchema`. Anything a renderer consumes
 * belongs in `widgetComponentProps` and is already carried. `limit` appears here
 * rather than relying on the Cube path's `limit` handling because the readmodel
 * branch returns before it.
 */
const READMODEL_WIDGET_KEYS = [ 'collection', 'filter', 'limit' ] as const;

/** Carry the readmodel binding across the seam, dropping nothing and inventing nothing. */
const copyReadmodelKeys = (
  source: Record<string, any>,
  target: Record<string, any>
): void => {
  READMODEL_WIDGET_KEYS.forEach(( key ) => setIfPresent( target, key, source[key]));
};

// ---------------------------------------------------------------------------
// builder form -> domain (the SAVE path)
// ---------------------------------------------------------------------------

const toDomainFilter = ( filter: FilterItem ): DomainFilter => {
  const operator = FILTER_OPERATOR_MAP[filter.operator] ?? filter.operator;
  const domainFilter: DomainFilter = { member: filter.field, operator };

  if ( DOMAIN_UNARY_OPERATORS.has( operator )) return domainFilter;

  const values = filter.values?.length
    ? [...filter.values]
    : filter.value !== undefined && filter.value !== '' ? [filter.value] : [];

  setIfPresent( domainFilter, 'values', values );
  return domainFilter;
};

const toDomainTimeDimension = ( dateRange: DateRangeItem ): DomainTimeDimension => {
  const timeDimension: DomainTimeDimension = { dimension: dateRange.field };

  setIfPresent( timeDimension, 'granularity', dateRange.granularity?.unit );

  // Mirrors `resolveDateRangeWindow`'s priority EXACTLY: an explicit custom
  // window wins over the relative token. Diverging here would mean the persisted
  // definition and the executed query disagree about which range is in force.
  if ( dateRange.start && dateRange.end ) {
    timeDimension.dateRange = [ dateRange.start, dateRange.end ];
  } else {
    setIfPresent( timeDimension, 'dateRange', dateRange.relativeRange );
  }

  // The INTENT, never the resolved pair — see the module docblock. A persisted
  // `compareDateRange` would freeze the window at authoring time.
  if ( dateRange.compare ) timeDimension.compare = true;

  return timeDimension;
};

/**
 * Builder-form widget -> the domain/wire widget the aggregate accepts.
 *
 * ⚠️ NOT idempotent, and the guard below is why. Fed an ALREADY-domain widget,
 * the dimension map (`d.field` on a `string`) would yield `undefined` for every
 * entry, `filter(Boolean)` would drop them all, and the widget would persist with
 * its dimensions silently gone — a chart that still renders and is now wrong. A
 * throw is the only acceptable behaviour: this is the seam, and crossing it twice
 * is a bug in the caller. Use `toCommandBody` when a layout is already in the
 * domain grammar.
 */
export const toDomainWidget = ( widget: WidgetDefinition ): DomainWidgetDefinition => {
  if (( widget as any ).dimensions?.some(( d: unknown ) => typeof d === 'string' )) {
    throw new Error(
      'toDomainWidget received a widget that is ALREADY in the domain grammar '
      + '(dimensions are strings, not { field }). Applying the adapter twice would '
      + 'silently drop every dimension. Use toCommandBody for an already-domain layout.'
    );
  }

  const domain: DomainWidgetDefinition = {
    id: widget.id,
    name: widget.name,
    type: widget.type,
    widgetComponentProps: widget.widgetComponentProps ?? {}
  };

  setIfPresent( domain, 'title', widget.title );
  setIfPresent( domain, 'description', widget.description );

  // A DATALESS widget (`text`) extends `UiWidgetBaseSchema`, NOT
  // `UiWidgetDataBaseSchema` — it has no `measures`/`dimensions`/`filters` keys at
  // all. Emitting them (even as `[]`, which the builder form always carries) is an
  // additionalProperty and a 400.
  if ( isDatalessWidgetType( widget.type )) return domain;

  // TV1-2291 (§6) — a READMODEL widget is the dataless case plus a binding of its
  // own: `UiWidgetReadmodelBaseSchema` is `UiWidgetBaseSchema` + collection/filter/
  // limit. Same reason to return early (no Cube keys may be emitted), plus the five
  // keys that ARE its data source.
  if ( isReadmodelWidgetType( widget.type )) {
    copyReadmodelKeys( widget, domain );
    return domain;
  }

  setIfPresent( domain, 'measures', widget.measures?.filter( Boolean ));
  setIfPresent(
    domain,
    'dimensions',
    ( widget.dimensions ?? []).map(( d: DimensionItem ) => d.field ).filter( Boolean )
  );
  setIfPresent(
    domain,
    'filters',
    ( widget.filters ?? []).filter( f => Boolean( f.field )).map( toDomainFilter )
  );
  setIfPresent(
    domain,
    'orderBy',
    ( widget.orderBy ?? [])
      .filter(( o: OrderByItem ) => Boolean( o.field ))
      .map(( o: OrderByItem ) => ({ field: o.field, direction: o.direction }))
  );

  if ( widget.dateRange ) {
    domain.timeDimensions = [toDomainTimeDimension( widget.dateRange )];
  }

  if ( typeof widget.limit === 'number' ) domain.limit = widget.limit;
  if ( typeof widget.offset === 'number' ) domain.offset = widget.offset;

  return domain;
};

// ---------------------------------------------------------------------------
// domain -> builder form (the LOAD / RENDER path)
// ---------------------------------------------------------------------------

const toBuilderFilter = ( filter: DomainFilter ): FilterItem => {
  const builderFilter: FilterItem = {
    field: filter.member,
    operator: DOMAIN_TO_BUILDER_OPERATOR[filter.operator] ?? ( filter.operator as FilterOperator )
  };

  if ( filter.values?.length ) {
    builderFilter.values = [...filter.values];
    // `value` mirrors the first entry so the Filters section's single-value editor
    // has something to show. `builderFilterToCubeFilter` prefers `values`, so a
    // multi-value filter is never narrowed by this.
    builderFilter.value = filter.values[0];
  }

  return builderFilter;
};

const toBuilderDateRange = ( timeDimension: DomainTimeDimension ): DateRangeItem => {
  const dateRange: DateRangeItem = { field: timeDimension.dimension, relativeRange: '' };

  if ( timeDimension.granularity ) {
    // `granularity.value` is dead on the query path (only `.unit` is read), so `1`
    // is not a fabrication with consequences — it is the field's only reader-free
    // slot. `granularity` stays ABSENT when the domain omits it: a KPI totals its
    // window and must not be bucketed.
    dateRange.granularity = { value: 1, unit: timeDimension.granularity as DateRangeItemGranularityUnitLike };
  }

  // A RESOLVED `compareDateRange` (legacy, or a hand-authored definition) is
  // normalised back to the intent: take its current window and recompute the prior
  // one at render time. This is deliberate — a stored resolved pair is a frozen
  // window, and `priorPeriodOf` reproduces exactly the immediately-preceding
  // period the pair encoded.
  const range = timeDimension.dateRange ?? timeDimension.compareDateRange?.[0];

  if ( Array.isArray( range )) {
    dateRange.relativeRange = 'custom';
    dateRange.start = range[0];
    dateRange.end = range[1];
  } else if ( typeof range === 'string' ) {
    dateRange.relativeRange = range;
  }

  if ( timeDimension.compare || timeDimension.compareDateRange?.length ) {
    dateRange.compare = true;
  }

  return dateRange;
};

/** Domain/wire widget -> the builder form the renderer and the builder eat. */
export const toBuilderWidget = ( widget: DomainWidgetDefinition ): WidgetDefinition => {
  const builder: WidgetDefinition = {
    id: widget.id,
    name: widget.name,
    cube: '',
    measures: [],
    dimensions: [],
    filters: [],
    orderBy: [],
    dateRange: null,
    type: widget.type as WidgetType,
    widgetComponentProps: widget.widgetComponentProps ?? {}
  };

  if ( widget.title ) builder.title = widget.title;
  if ( widget.description ) builder.description = widget.description;

  // `parseWidgetDefinition` exempts dataless types from the "cube + at least one
  // measure" rule, so the empty scaffolding above is exactly right for `text`.
  if ( isDatalessWidgetType( widget.type )) return builder;

  // TV1-2291 (§6) — a READMODEL widget never reaches `parseWidgetDefinition` at all:
  // `getWidgetData` branches on the type before it. The empty Cube scaffolding above
  // is therefore inert, and the only thing that matters is that the binding SURVIVES
  // — `toBuilderWidget` builds a fresh object, so a key not copied here is a key the
  // fetch branch will never see.
  if ( isReadmodelWidgetType( widget.type )) {
    copyReadmodelKeys( widget, builder );
    return builder;
  }

  builder.cube = deriveCube( widget );
  builder.measures = [...( widget.measures ?? [])];
  builder.dimensions = ( widget.dimensions ?? []).map(( field: string ): DimensionItem => ({ field }));
  builder.filters = ( widget.filters ?? []).map( toBuilderFilter );
  builder.orderBy = ( widget.orderBy ?? []).map(( o ): OrderByItem => ({ field: o.field, direction: o.direction }));

  const timeDimension = widget.timeDimensions?.[0];
  if ( timeDimension ) builder.dateRange = toBuilderDateRange( timeDimension );

  if ( typeof widget.limit === 'number' ) builder.limit = widget.limit;
  if ( typeof widget.offset === 'number' ) builder.offset = widget.offset;

  return builder;
};

// (local alias so the granularity cast reads honestly — the domain enum is a
// superset of nothing: both sides now carry all eight of Cube's units.)
type DateRangeItemGranularityUnitLike = NonNullable<DateRangeItem['granularity']>['unit'];

// ---------------------------------------------------------------------------
// Layout-level entry points (the two call sites)
// ---------------------------------------------------------------------------

/** The ONLY keys `UiWidgetsLayoutSchema` permits. It is `additionalProperties: false`. */
const DOMAIN_LAYOUT_KEYS = [
  'id', 'parentId', 'type', 'color', 'icon', 'name', 'description', 'widgets', 'device', 'cols', 'widthPx'
] as const;

/**
 * The GEOMETRY keys of the layout's widget envelope — and only those.
 *
 * ⚠️ This list exists to be filtered by `typeof === 'number'` (see
 * `pickWidgetEnvelope`). Do NOT add a non-numeric envelope key here: the filter is
 * what stops an `undefined`/`null` `x` from reaching a schema that types all four
 * as `S.number()`, and widening it to "any defined value" would remove that
 * protection from four fields to carry one. A new envelope key of another type
 * gets its OWN guarded copier, exactly as `tint` does below.
 */
const DOMAIN_LAYOUT_WIDGET_GEOMETRY_KEYS = [ 'x', 'y', 'width', 'height' ] as const;

/**
 * TV1-2291 (t34) — THE CARD TINT, carried across the seam in BOTH directions.
 *
 * `tint` rides on the layout WRAPPER, beside `x`/`y`/`width`/`height` (ticket 20,
 * and see `WidgetCard/tints.ts` for why it is not on the definition). It is
 * therefore an envelope key — but it is a STRING, so the geometry filter above
 * silently dropped it on every save. The tint survived only in memory (the wrapper
 * spread in `toBuilderLayout` plus `layoutState`), which is why picking a colour
 * looked like it worked and was gone on the next reload.
 *
 * Two properties this guard has to hold, and both matter:
 *
 *  1. **It validates against the TOKEN SET, not `typeof === 'string'`.** The
 *     domain declares `tint` as a seven-token enum, and `UiWidgetsLayoutSchema` is
 *     `additionalProperties: false` with no `removeAdditional` on PV3's ajv — so an
 *     unrecognised token is a hard ajv failure at `handleLayoutChange`, surfacing
 *     as a save the user cannot diagnose. Dropping it here degrades to "no tint",
 *     which is exactly what `resolveWidgetTint` already renders for it.
 *  2. **It never writes `tint: undefined`.** An explicitly-present `undefined` is
 *     still a present key to `Object.keys`/ajv, and this schema forbids anything it
 *     does not declare. Absent means absent.
 *
 * `isWidgetTint` is imported rather than re-spelled: the token list has ONE
 * definition (`WidgetCard/tintTokens.ts`, re-exported by `tints.ts`), and a second
 * copy here is precisely how a token added to the palette ends up silently dropped
 * by the save path — the same class of defect as the one this whole guard closes.
 */
const setTintIfValid = ( source: Record<string, any>, target: Record<string, any> ): void => {
  // `isTintValue`, not `isWidgetTint` (2026-08-17): a tint is now EITHER one of the
  // seven names OR a validated `#rrggbb`, and the domain's `anyOf` accepts both.
  // Guarding on the token set alone would silently drop every custom colour on the
  // way to the wire — the colour would appear to work in-session and be gone on
  // reload, which is exactly the t34 defect this guard was written to close, just
  // with a different value shape.
  if ( isTintValue( source.tint )) target.tint = source.tint;
};

/**
 * The layout body the aggregate accepts.
 *
 * `UiWidgetsLayoutType` — the REAL domain type, not the structural `DomainLayout`
 * above. The generated client library types `AddUiWidgetLayout`'s body as the
 * precise 10-branch discriminated union, and the whole point of this module is to
 * produce something that satisfies it. Returning a loose `type: string` /
 * `widgetComponentProps: Record<string, any>` shape would hand the caller a body
 * TypeScript could not check — which is how the grammar drifted in the first
 * place.
 *
 * The cast happens exactly once, here, and it is the ONLY unchecked step in the
 * module. It is backed by the ajv oracle in
 * `src/tests/src/analytics/widgetGrammar.adapter.test.ts`, which validates this
 * function's real output against the real `AddUiWidgetLayoutCommandSchema` under
 * PV3's real ajv config — a far stronger guarantee than structural assignability,
 * because it is the same check the server performs.
 */
export type DomainLayoutBody = UiWidgetsLayoutType;

/** Copy the layout envelope, keeping ONLY the keys the schema declares. */
const pickDomainLayoutKeys = (
  source: Record<string, any>,
  mapWidget: ( widget: Record<string, any> ) => Record<string, any>
): DomainLayoutBody => {
  const domain = {} as Record<string, any>;

  DOMAIN_LAYOUT_KEYS.forEach(( key ) => {
    if ( key === 'widgets' ) return;
    if ( source[key] !== undefined ) domain[key] = source[key];
  });

  if ( Array.isArray( source.widgets )) {
    domain.widgets = source.widgets.map( mapWidget );
  }

  return domain as unknown as DomainLayoutBody;
};

/**
 * Keep only the declared envelope keys (`additionalProperties: false` there too):
 * `x`/`y`/`width`/`height` behind the numeric filter, then `tint` behind its own
 * token-set guard. Two filters, because the two have different failure modes — see
 * `DOMAIN_LAYOUT_WIDGET_GEOMETRY_KEYS` and `setTintIfValid`.
 */
const pickWidgetEnvelope = ( widget: Record<string, any> ): Record<string, any> => {
  const envelope: Record<string, any> = {};
  DOMAIN_LAYOUT_WIDGET_GEOMETRY_KEYS.forEach(( key ) => {
    if ( typeof widget[key] === 'number' ) envelope[key] = widget[key];
  });
  setTintIfValid( widget, envelope );
  return envelope;
};

/**
 * An ALREADY-domain layout -> the exact body `AddUiWidgetLayout` /
 * `ChangeUiWidgetLayout` accept. **Idempotent**, and grammar-preserving: it moves
 * no widget between grammars, it only drops keys the schema forbids.
 *
 * The whitelist is load-bearing, not defensive tidiness. A layout read back from
 * `UiWidgetLayoutsReadmodel` carries fields the command schema does not declare —
 * PV3's `lastEvent`, and `isActive`, which the readmodel's Activate/Deactivate
 * projectors `upsert` onto the row. `UiWidgetsLayoutSchema` is
 * `additionalProperties: false`, so echoing either straight back is its own 400:
 * a layout that had ever been deactivated could never be edited again.
 *
 * This is what every command dispatch in `Reports.tsx` goes through, including the
 * one coming back out of `WidgetLayoutComponent`.
 */
export const toCommandBody = <T extends Record<string, any>>( layout: T ): DomainLayoutBody =>
  pickDomainLayoutKeys( layout, ( widget ) => ({
    ...pickWidgetEnvelope( widget ),
    definition: widget.definition
  }));

/**
 * Builder-form layout -> the domain grammar + the command whitelist, in one step.
 *
 * The SAVE half of the seam. `WidgetLayoutComponent` is its only caller — nothing
 * else holds a builder-form layout.
 */
export const toDomainLayout = <T extends { widgets?: unknown[] }>( layout: T ): DomainLayoutBody =>
  pickDomainLayoutKeys( layout as Record<string, any>, ( widget ) => ({
    ...pickWidgetEnvelope( widget ),
    definition: toDomainWidget( widget.definition as WidgetDefinition )
  }));

/**
 * Domain layout (readmodel snapshot, curated default, or LLM-generated preview)
 * -> the builder-form layout the renderer, the auto-grid and the editor eat.
 *
 * Non-domain keys on the source are preserved here on purpose: the render path
 * does not care about them, and `toDomainLayout` strips them again on the way
 * out. That keeps `lastEvent` available to any caller that still wants it.
 *
 * TV1-2291 (t34) — `tint` is the ONE envelope key that is NOT simply carried by
 * that spread. It is re-applied through `setTintIfValid`, which makes the LOAD
 * half of the seam symmetric with the save half: the seven tokens survive, and an
 * unrecognised one (an older build, a hand-edited fixture, the AI preview path) is
 * dropped HERE rather than being handed to the editor, put back on the wire by
 * `toDomainLayout` and rejected by ajv one save later. `resolveWidgetTint` guards
 * the render independently; this guards the DATA.
 */
export const toBuilderLayout = <T extends { widgets?: unknown[] }>( layout: T ): T & { widgets: any[] } => {
  const source = layout as Record<string, any>;

  const widgets = Array.isArray( source.widgets )
    ? source.widgets.map(( widget: Record<string, any> ) => {
      // `tint` is dropped from the spread and re-added only when it validates, so
      // an unknown token leaves NO key behind (not `tint: undefined` — a present
      // key is a present key to `additionalProperties: false`).
      const { tint: _unvalidatedTint, ...rest } = widget;
      const builderWidget: Record<string, any> = {
        ...rest,
        definition: toBuilderWidget( widget.definition as DomainWidgetDefinition )
      };
      setTintIfValid( widget, builderWidget );
      return builderWidget;
    })
    : [];

  return { ...( layout as any ), widgets };
};
